"use client";

import React, { useState, useEffect } from 'react';
import { Label } from '@/components/ui/label';
import { Input } from '@/components/ui/input';
import { Checkbox } from '@/components/ui/checkbox';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Button } from '@/components/ui/button';
import { Plus, X } from '@/components/icons';
import { useToast } from '@/components/ui/use-toast';

interface AvailabilitySlot {
  start: string;
  end: string;
}

interface DaySchedule {
  [key: string]: AvailabilitySlot[];
}

interface PrivateClassesFieldsProps {
  details?: Record<string, any>;
  onDetailsChange?: (details: Record<string, any>) => void;
}

const DAYS_OF_WEEK = ['Lunes', 'Martes', 'Miércoles', 'Jueves', 'Viernes', 'Sábado', 'Domingo'];
const DAY_KEYS = ['monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday', 'sunday'];

const DISCIPLINES = [
  'Matemáticas',
  'Español/Lengua',
  'Inglés',
  'Ciencias',
  'Historia',
  'Física',
  'Química',
  'Biología',
  'Programación',
  'Música',
  'Arte',
  'Educación Física',
  'Otra',
];

const LEVELS = [
  'Principiante',
  'Intermedio',
  'Avanzado',
  'Todos los niveles',
];

const PrivateClassesFields: React.FC<PrivateClassesFieldsProps> = ({ details = {}, onDetailsChange }) => {
  const [isGlobal, setIsGlobal] = useState<boolean>(details?.is_global ?? false);
  const [discipline, setDiscipline] = useState<string>(details?.discipline ?? '');
  const [level, setLevel] = useState<string>(details?.level ?? '');
  const [sessionDuration, setSessionDuration] = useState<number>(details?.session_duration_minutes ?? 60);
  const [maxStudents, setMaxStudents] = useState<number>(details?.max_students_per_session ?? 1);
  const [schedule, setSchedule] = useState<DaySchedule>(details?.availability_schedule ?? {});
  const { toast } = useToast();

  // Notify parent when details change
  useEffect(() => {
    if (onDetailsChange) {
      onDetailsChange({
        is_global: isGlobal,
        discipline,
        level,
        session_duration_minutes: sessionDuration,
        max_students_per_session: maxStudents,
        availability_schedule: schedule,
      });
    }
  }, [isGlobal, discipline, level, sessionDuration, maxStudents, schedule, onDetailsChange]);

  const addTimeSlot = (dayKey: string) => {
    setSchedule(prev => ({
      ...prev,
      [dayKey]: [...(prev[dayKey] || []), { start: '09:00', end: '10:00' }],
    }));
  };

  const removeTimeSlot = (dayKey: string, index: number) => {
    setSchedule(prev => {
      const updated = [...(prev[dayKey] || [])];
      updated.splice(index, 1);
      return { ...prev, [dayKey]: updated.length > 0 ? updated : undefined };
    });
  };

  const updateTimeSlot = (dayKey: string, index: number, field: 'start' | 'end', value: string) => {
    setSchedule(prev => {
      const updated = [...(prev[dayKey] || [])];
      updated[index] = { ...updated[index], [field]: value };
      return { ...prev, [dayKey]: updated };
    });
  };

  return (
    <Card className="mt-6">
      <CardHeader>
        <CardTitle className="text-lg">Detalles de la clase particular</CardTitle>
      </CardHeader>
      <CardContent className="space-y-6">
        {/* Global / Local Selection */}
        <div className="space-y-3">
          <Label className="text-base font-semibold">Modalidad</Label>
          <div className="space-y-2">
            <div className="flex items-center space-x-2">
              <Checkbox
                id="is_global"
                checked={isGlobal}
                onCheckedChange={(checked) => setIsGlobal(checked as boolean)}
              />
              <Label htmlFor="is_global" className="font-normal cursor-pointer">
                Clase global (Online)
              </Label>
            </div>
            <div className="flex items-center space-x-2">
              <Checkbox
                id="is_local"
                checked={!isGlobal}
                onCheckedChange={(checked) => setIsGlobal(!checked)}
              />
              <Label htmlFor="is_local" className="font-normal cursor-pointer">
                Clase local (En mi ubicación)
              </Label>
            </div>
          </div>
        </div>

        {/* Discipline */}
        <div className="space-y-2">
          <Label htmlFor="discipline">Disciplina / Materia *</Label>
          <Select value={discipline} onValueChange={setDiscipline}>
            <SelectTrigger id="discipline">
              <SelectValue placeholder="Selecciona una disciplina" />
            </SelectTrigger>
            <SelectContent>
              {DISCIPLINES.map(d => (
                <SelectItem key={d} value={d}>{d}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>

        {/* Level */}
        <div className="space-y-2">
          <Label htmlFor="level">Nivel de enseñanza</Label>
          <Select value={level} onValueChange={setLevel}>
            <SelectTrigger id="level">
              <SelectValue placeholder="Selecciona un nivel" />
            </SelectTrigger>
            <SelectContent>
              {LEVELS.map(l => (
                <SelectItem key={l} value={l}>{l}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>

        {/* Session Duration */}
        <div className="space-y-2">
          <Label htmlFor="duration">Duración de la clase (minutos)</Label>
          <Input
            id="duration"
            type="number"
            min="30"
            max="240"
            step="15"
            value={sessionDuration}
            onChange={(e) => setSessionDuration(Math.max(30, parseInt(e.target.value) || 60))}
          />
        </div>

        {/* Max Students */}
        <div className="space-y-2">
          <Label htmlFor="maxStudents">Máximo de estudiantes por clase</Label>
          <Input
            id="maxStudents"
            type="number"
            min="1"
            max="20"
            value={maxStudents}
            onChange={(e) => setMaxStudents(Math.max(1, parseInt(e.target.value) || 1))}
          />
        </div>

        {/* Availability Schedule */}
        <div className="space-y-3">
          <Label className="text-base font-semibold">Horarios disponibles</Label>
          <p className="text-sm text-secondary-foreground">
            Selecciona los días y horarios cuando estés disponible para dar clase
          </p>

          <div className="space-y-4">
            {DAY_KEYS.map((dayKey, dayIndex) => {
              const dayLabel = DAYS_OF_WEEK[dayIndex];
              const slots = schedule[dayKey] || [];

              return (
                <div key={dayKey} className="border rounded-lg p-4 space-y-3">
                  <div className="flex items-center justify-between">
                    <h4 className="font-semibold text-sm">{dayLabel}</h4>
                    <Button
                      type="button"
                      size="sm"
                      variant="outline"
                      onClick={() => addTimeSlot(dayKey)}
                      className="gap-1"
                    >
                      <Plus className="w-4 h-4" />
                      Agregar horario
                    </Button>
                  </div>

                  {slots.length > 0 ? (
                    <div className="space-y-2">
                      {slots.map((slot, idx) => (
                        <div key={idx} className="flex items-center gap-2 bg-secondary/20 p-2 rounded">
                          <Input
                            type="time"
                            value={slot.start}
                            onChange={(e) => updateTimeSlot(dayKey, idx, 'start', e.target.value)}
                            className="w-24 text-xs"
                          />
                          <span className="text-sm font-semibold">-</span>
                          <Input
                            type="time"
                            value={slot.end}
                            onChange={(e) => updateTimeSlot(dayKey, idx, 'end', e.target.value)}
                            className="w-24 text-xs"
                          />
                          <Button
                            type="button"
                            size="sm"
                            variant="ghost"
                            onClick={() => removeTimeSlot(dayKey, idx)}
                            className="ml-auto"
                          >
                            <X className="w-4 h-4 text-destructive" />
                          </Button>
                        </div>
                      ))}
                    </div>
                  ) : (
                    <p className="text-xs text-muted-foreground italic">Sin horarios configurados</p>
                  )}
                </div>
              );
            })}
          </div>
        </div>
      </CardContent>
    </Card>
  );
};

export default PrivateClassesFields;
